import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useStripe } from '@stripe/react-stripe-js'
import useCart from '@/public/hooks/use-cart'
import { selectVerified } from '@/public/redux/recaptchaSlice'
import {
  selectError,
  selectLoading,
} from '@/public/redux/cartSlice'
import CartItem from '@/public/cart/CartItem'
import VirtualVisitCartItem from '@/public/cart/VirtualVisitCartItem'
import CartLedger from '@/public/cart/CartLedger'
import MixedLabCompanyBanner from '@/public/cart/MixedLabCompanyBanner'
import LabCompanyNoticeBanner from '@/public/cart/LabCompanyNoticeBanner'
import ModalButton from '@/public/ModalButton'
import LoadingOverlay from '@/public/LoadingOverlay'
import DiscountCode from '@/public/cart/DiscountCode'

export default function Cart({
  paymentUnavailable = false,
  termsPolicyModalId,
  activeDiscountCodePresent,
  selectedState,
  monthlyMembership,
  membershipPlansEnabled = false,
  annualMembership,
  userSponsoredAllowance,
  virtualVisits,
}) {
  const stripe = useStripe()
  const verified = useSelector(selectVerified)
  const paymentError = useSelector(selectError)
  const loading = useSelector(selectLoading)
  const { cart } = useCart()
  // No Stripe yet, or a Stripe that could not draw the payment field: either
  // way there is nothing to pay with.
  const cannotPay = !stripe || paymentUnavailable

  // Once an applied code has put the field on screen, keep it there: removing
  // that code must leave the shopper able to enter a different one, even when
  // no local code is on offer (a Stripe promotion is applied without one).
  const [discountFieldShown, setDiscountFieldShown] = useState(false)
  useEffect(() => {
    if (cart?.discount_code) { setDiscountFieldShown(true) }
  }, [cart?.discount_code])

  if (!cart) {
    return null
  }

  const restrictedCartItems = cart?.contents?.filter((item) => (
    item.data.state_sales_restrictions?.includes(selectedState)
  ))
  const cartContainsLabOrTest = cart?.contents?.some((cartItem) => cartItem.data.category === 'lab' || cartItem.data.category === 'test')

  return (
    <div className="col-lg-6 col-xl-5 cart-col">
      <h2 className="underline mb-5">Cart</h2>

      <div className="cart-pane cart-checkout loading-overlay--container">
        <LoadingOverlay show={loading} className="tan" />

        <LabCompanyNoticeBanner cart={cart} />
        <MixedLabCompanyBanner show={Boolean(cart?.mixed_lab_company)} />

        <div className="cart-list pe-0">
          {cart?.contents?.map((item, index) => {
            const isVirtualVisit = item.data.category === 'virtual_visit'

            return isVirtualVisit ? (
              <VirtualVisitCartItem
                key={`cart-item-${index}`}
                data={item.data}
                virtualVisit={virtualVisits[item.data.id]}
                expiresAtTimestamp={item.expires_at_timestamp}
                showTimer={index === 0}
              />
            ) : (
              <CartItem
                key={`cart-item-${index}`}
                data={item.data}
                quantity={item.quantity}
                quantifiable={item.quantifiable}
                subscription={item.subscription}
                pickupOrDelivery={item?.pickup_or_delivery}
                cartContainsLabOrTest={cartContainsLabOrTest}
                monthlyMembership={monthlyMembership}
                membershipPlansEnabled={membershipPlansEnabled}
                annualMembership={annualMembership}
                labCompanyName={item.lab_company_name}
              />
            )
          })}
        </div>

        <CartLedger userSponsoredAllowance={userSponsoredAllowance} />

        {(activeDiscountCodePresent || cart.discount_code || discountFieldShown)
          && <DiscountCode cart={cart} />}
      </div>

      <div className="cart-footer pe-0">
        {restrictedCartItems && restrictedCartItems.length > 0 && (
          <div className="text-danger">
            <hr />
            <h6 className="text-danger">Oh no!</h6>
            <p>
              We're very sorry, but you have
              {restrictedCartItems.length === 1 ? ' an item' : ' items'}
              {' '}
              in your cart that we're not allowed to sell to you due to sales restrictions
              in the state to which you're shipping it.
            </p>
            <p>
              Please remove the following
              {restrictedCartItems.length === 1 ? ' item' : ' items'}
              {' '}
              to continue checking out:
            </p>
            <ul>
              {restrictedCartItems.map((item) => (
                <li key={item.data.name}>{item.data.name}</li>))}
            </ul>
          </div>
        )}
        <p>
          By clicking
          {' '}
          {cart.contains_subscription ? 'Continue Checkout ' : 'Place Order '}
          you agree to our
          <ModalButton
            id={termsPolicyModalId}
            className="btn btn-link ps-1 mb-1"
            title="Click to view our terms and conditions"
            text="Terms & Conditions*"
          />
        </p>

        <div className="cart-footer-actions">
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={!!(!verified || loading || cannotPay || restrictedCartItems.length > 0)}
          >
            {cart.contains_subscription ? 'Continue Checkout' : 'Place Order'}
          </button>
        </div>

        {paymentError
          && <p className="text-danger text-center mt-5 mb-n5 px-5">{typeof paymentError === 'string' ? paymentError : paymentError.message}</p>}
      </div>
    </div>
  )
}
